import {
  TableBody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Table,
  TableActionTh,
  TableActionTd,
} from '@widgets/table';
import Paginator, { DEFAULT_PER_PAGE } from '@widgets/paginator';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import Section from '@widgets/components/section';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBreadcrumbs } from '@app/redux/features/breadcrumbsSlice';
import { BreadCrumbIcon, BreadCrumbType } from '@entities/util/IBreadCrumb';
import { ListLoader, Loader } from '@shared/uikit/loader';
import { selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import EmptyList from '@widgets/empty-list';
import {
  IAccountDynamicsList,
  useAccountDynamicsList,
} from '@app/queries/account-dynamics/useAccountDynamicsList';
import AccountDynamicsFilter, { Filter } from '@pages/AccountsDynamics/List/filter';
import AccountDynamicsLinesChart from '@pages/AccountsDynamics/components/line-chart';
import { GrayButton } from '@shared/uikit/button';
import IconDownload from '@shared/icons/IconDownload';
import { useUrlParams } from '@app/hooks/useUrlParams';
import {
  useFilters,
  useLimit,
  useListParamsAndPersistToUrl,
  usePage,
  useSearch,
  useSort,
} from '@app/hooks/useList';
import { useDownloadAccountDynamicsList } from '@app/queries/account-dynamics/useDownloadAccountDynamicsList';
import Search from '@widgets/components/search';
import { useAccountDynamicsTotalHashrate } from '@app/queries/account-dynamics/useAccountDynamicsTotalHashrate';
import { Pagination } from '@widgets/index';

const AccountsDynamics = () => {
  const dispatch = useDispatch();

  const accountDynamicsListFetcher = useAccountDynamicsList();
  const downloadAccountDynamicsListFetcher = useDownloadAccountDynamicsList();
  const accountDynamicsTotalHashrateFetcher = useAccountDynamicsTotalHashrate();

  const { urlParams } = useUrlParams();

  const page = usePage(urlParams, 1);
  const perPage = useLimit(urlParams, DEFAULT_PER_PAGE);
  const filtering = useFilters(urlParams, []);
  const sorting = useSort(urlParams);
  const searching = useSearch(urlParams);
  useListParamsAndPersistToUrl(page, perPage, filtering, sorting, searching);

  const [accountDynamicsList, setAccountDynamicsList] = useState<IAccountDynamicsList | null>(null);
  const [bulkItems, setBulkItems] = useState<number[]>([]);
  const [totalHashrate, setTotalHashrate] = useState<number | null>(null);

  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Sales DB',
          type: BreadCrumbType.LINK,
          link: '/main',
          icon: BreadCrumbIcon.VIA_BTC,
        },
        { title: 'Динамика по аккаунтам', type: BreadCrumbType.TEXT },
      ])
    );
  }, []);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      setAccountDynamicsList(null);
      const data = await accountDynamicsListFetcher.fetch({
        ...filtering.value,
        page: page.value ?? 1,
        search: searching.value,
        sort: sorting.value,
        perPage: perPage.value ?? DEFAULT_PER_PAGE,
      });

      setAccountDynamicsList(data);
    })();
  }, [
    page.value,
    perPage.value,
    filtering.value,
    sorting.value?.column,
    sorting.value?.type,
    searching.value,
  ]);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const data = await accountDynamicsTotalHashrateFetcher.fetch({
        ...filtering.value,
        page: page.value ?? 1,
        search: searching.value,
        sort: sorting.value,
      });

      setTotalHashrate(data.hashrate);
    })();
  }, [filtering.value, searching.value]);

  const handlePageClick = useCallback((selectedItem: { selected: number }) => {
    page.setValue(selectedItem.selected + 1);
  }, []);

  const onChangePerPage = useCallback(
    (count: number) => {
      perPage.setValue(count);
    },
    [perPage.value]
  );

  const onDownload = async () => {
    await downloadAccountDynamicsListFetcher.fetch({
      ...filtering.value,
      sort: sorting.value,
    });
  };

  return (
    <>
      <div className="pr-[10px] sticky top-[90px] bg-[#141316] z-50">
        <div className="flex justify-between items-center w-full pb-[30px]">
          <AccountDynamicsFilter
            filters={filtering.value}
            onChangeFilters={(values: Filter) => filtering.setValue(values)}
            onReset={() => {
              page.setValue(1);
              perPage.setValue(DEFAULT_PER_PAGE);
              sorting.setValue(null);
              filtering.setValue([]);
            }}
          />
          <div className="flex gap-[10px] pl-[10px] self-start">
            <div className="flex gap-[10px]">
              <GrayButton
                onClick={() => onDownload()}
                title={
                  <div className="flex gap-[10px] items-center fill-[white]">
                    <IconDownload className="w-[11px] h-[14px] fill-[#D9D9D9]" />
                  </div>
                }
              />
            </div>
            <Search search={''} onSearch={searching.setValue} />
          </div>
        </div>
      </div>

      <Section>
        <AccountDynamicsLinesChart filter={filtering.value} search={searching.value} />
      </Section>

      {null !== accountDynamicsList ? (
        <>
          <div className="flex items-center justify-between w-full mt-[40px]">
            <span className="font-semibold text-[20px]">Майнинг аккаунты</span>
            <div
              className="flex gap-[10px] h-[50px] self-stretch border-t border-x border-[#95959540]
                            rounded-t-[16px] z-10 relative bg-[#141316]"
            >
              <div className="absolute w-full h-[20px] bg-[#141316] -bottom-[2px] left-0"></div>

              {accountDynamicsTotalHashrateFetcher.isSuccess ? (
                <div className="flex px-[30px] justify-between items-center">
                  <div className="text-[12px] text-[#777777]">Всего</div>
                  <div className="flex justify-end items-end gap-[5px]">
                    <span className="text-[18px] ml-[10px]">{totalHashrate}</span>&nbsp;
                    <span className="text-[12px] text-[#777777] mb-[3px]">Ph</span>
                  </div>
                </div>
              ) : (
                <div className="w-[208px] flex items-center justify-center">
                  <Loader size={20} loading={true} />
                </div>
              )}
            </div>
          </div>
          <div
            className="py-[30px] px-[24px] mb-[20px] border border-opacity-30 solid rounded-[16px] border-[#959595]
                        h-[100%] w-[100%] rounded-tr-[0px] relative"
          >
            {accountDynamicsList.items.length ? (
              <>
                <div className="overflow-y-auto flex-1 max-h-[calc(100vh-330px)]">
                  <Table>
                    <TableThead>
                      <TableTr>
                        <TableActionTh
                          className="w-[3%]"
                          onClick={() => {
                            if (accountDynamicsList.items.length === bulkItems.length) {
                              setBulkItems([]);
                            } else {
                              setBulkItems(accountDynamicsList.items.map((item, index) => index));
                            }
                          }}
                        />
                        <TableTh
                          title="Аккаунт"
                          className="w-[17%]"
                          sortName="account"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                        <TableTh
                          title="Хешрейт"
                          className="w-[10%]"
                          sortName="hashrate"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                        <TableTh
                          title="Начало майнинга в группе"
                          className="w-[15%]"
                          sortName="group_started_date"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                        <TableTh
                          title="Дата начала майнинга"
                          className="w-[15%]"
                          sortName="account_started_date"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                        <TableTh
                          title="Менеджер"
                          className="w-[12%]"
                          sortName="manager_name"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                        <TableTh
                          title="Клиент"
                          className="w-[15%]"
                          sortName="client"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                        <TableTh
                          title="Источник"
                          className="w-[13%]"
                          sortName="source"
                          sort={sorting.value}
                          onSort={sorting.setValue}
                        />
                      </TableTr>
                    </TableThead>
                    <TableBody>
                      {accountDynamicsList &&
                        accountDynamicsList.items.map((item, index) => (
                          <TableTr key={index}>
                            <TableActionTd
                              items={bulkItems}
                              value={index}
                              onChange={setBulkItems}
                            />
                            <TableTd
                              title={
                                <ValueWithSign value={item.account} color="bg-gradient-yellow" />
                              }
                            />
                            <TableTd title={item.hashrate + ' PH'} />
                            <TableTd title={item.group_started_date} />
                            <TableTd title={item.account_started_date} />
                            <TableTd title={item.manager_name} />
                            <TableTd
                              title={
                                <div className="flex items-center gap-2 cursor-pointer">
                                  <ValueWithSign
                                    value={item.client ?? 'Нет клиента'}
                                    color="bg-gradient-blue"
                                  />
                                </div>
                              }
                            />
                            <TableTd title={item.source} align="left" />
                          </TableTr>
                        ))}
                    </TableBody>
                  </Table>
                </div>
                <Pagination
                  className="pt-8 justify-end"
                  totalPages={accountDynamicsList.total_pages}
                  page={page.value ? page.value - 1 : 0}
                  perPage={perPage.value ?? DEFAULT_PER_PAGE}
                  onChangePerPage={onChangePerPage}
                  onChangePage={handlePageClick}
                  totalItems={accountDynamicsList?.total_items}
                />
              </>
            ) : (
              <EmptyList />
            )}
          </div>
        </>
      ) : (
        <ListLoader loading />
      )}
    </>
  );
};

export default AccountsDynamics;
